import React, { useState, useEffect, useMemo } from 'react';
import {
  UserPlus,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Phone,
  Globe,
  MapPin,
  Building2,
  Briefcase,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { allCountries, civilities } from '../data/quoteFormOptions';
import { getRegionOptions, getVilleOptions } from '../data/geoData';
import '../pages/AuthPage.css';

const REGISTER_STEPS = ['identity', 'profile', 'terms'];
const EDIT_STEPS = ['identity', 'profile'];

const STEP_LABELS = {
  identity: 'Informations & compte',
  profile: 'Type & communication',
  terms: 'Conditions',
};

const rowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '18px',
};

const radioGroupStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '12px',
};

const radioStyle = (selected) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 12px',
  border: `2px solid ${selected ? 'var(--teal)' : 'var(--gray-200)'}`,
  borderRadius: '12px',
  background: selected ? 'rgba(26, 122, 122, 0.08)' : '#fff',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: '700',
  color: selected ? 'var(--teal)' : 'var(--navy)',
});

const checkboxStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '10px 14px',
  border: '1px solid var(--gray-200)',
  borderRadius: '12px',
  background: '#fff',
  fontSize: '0.85rem',
  fontWeight: '600',
  color: 'var(--navy)',
  lineHeight: '1.4',
  cursor: 'pointer',
};

const selectStyle = { width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--navy)', fontSize: '0.95rem' };

const sectionDividerStyle = {
  margin: '20px 0 14px',
  paddingTop: '16px',
  borderTop: '1px solid var(--gray-200)',
  fontSize: '0.78rem',
  fontWeight: 800,
  color: 'var(--teal)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

export default function ClientRegisterForm({
  mode = 'register',
  initialValues,
  onSubmit,
  submitLabel,
  successMessage,
  onSuccess,
  title,
  subtitle,
  footer,
}) {
  const { t } = useLanguage();
  const editMode = mode === 'edit';
  const steps = editMode ? EDIT_STEPS : REGISTER_STEPS;

  const init = initialValues || {};
  const nameParts = String(init.name || '').trim().split(/\s+/);
  const [form, setForm] = useState({
    civility: init.civility || '',
    firstName: init.firstName || nameParts[0] || '',
    lastName: init.lastName || nameParts.slice(1).join(' ') || '',
    email: init.email || '',
    phone: init.phone || '',
    country: init.country || 'Tunisie',
    city: init.city || 'Tunis',
    region: init.region || '',
    address: init.address || '',
    username: init.username || '',
    password: '',
    confirmPassword: '',
    clientType: init.clientType || 'particulier',
    organizationName: init.organizationName || '',
    taxId: init.taxId || '',
    contactMode: init.contactMode || 'email',
    acceptTerms: false,
    acceptPrivacy: false,
    confirmInfo: false,
  });

  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState(false);

  const stepKey = steps[stepIdx];
  const isLastStep = stepIdx === steps.length - 1;
  const stepLabel = (key) => (key === 'identity' && editMode ? 'Informations personnelles' : STEP_LABELS[key]);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setTouched(true);
  };

  // Cascade Pays → Ville → Région (le formulaire stocke le nom du pays)
  const countryCode = useMemo(
    () => allCountries.find((c) => c.name === form.country || c.code === form.country)?.code || '',
    [form.country]
  );
  const villeOptions = useMemo(() => getVilleOptions(countryCode), [countryCode]);
  const regionOptions = useMemo(
    () => getRegionOptions(countryCode, form.city),
    [countryCode, form.city]
  );
  const regionRequired = regionOptions.length > 0;

  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return score;
  };

  const getPasswordError = (pwd) => {
    if (pwd.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères.';
    if (!/[A-Z]/.test(pwd)) return 'Le mot de passe doit contenir au moins une majuscule.';
    if (!/[a-z]/.test(pwd)) return 'Le mot de passe doit contenir au moins une minuscule.';
    if (!/[0-9]/.test(pwd)) return 'Le mot de passe doit contenir au moins un chiffre.';
    if (!/[^A-Za-z0-9]/.test(pwd)) return 'Le mot de passe doit contenir au moins un caractère spécial.';
    return '';
  };

  const getBlockError = (key) => {
    switch (key) {
      case 'personal':
        if (!form.civility || !form.firstName || !form.lastName || !form.email || !form.phone || !form.country || (regionRequired && !form.region)) {
          return 'Veuillez remplir tous les champs obligatoires de cette étape.';
        }
        if (form.firstName.trim().length < 2 || form.lastName.trim().length < 2) {
          return 'Le prénom et le nom doivent contenir au moins 2 caractères.';
        }
        if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Adresse e-mail invalide.';
        return '';
      case 'account':
        if (!form.username) return 'Veuillez choisir un nom d\'utilisateur.';
        {
          const pwdError = getPasswordError(form.password);
          if (pwdError) return pwdError;
        }
        if (form.password !== form.confirmPassword) return 'Les mots de passe ne correspondent pas.';
        return '';
      case 'type':
        if (!form.clientType) return 'Veuillez sélectionner un type de client.';
        return '';
      case 'contact':
        if (!form.contactMode) return 'Veuillez choisir un mode de contact.';
        return '';
      case 'terms':
        if (!form.acceptTerms || !form.acceptPrivacy) {
          return 'Vous devez accepter les conditions générales et la politique de confidentialité.';
        }
        if (!form.confirmInfo) {
          return 'Veuillez confirmer l\'exactitude des informations fournies.';
        }
        return '';
      default:
        return '';
    }
  };

  const getStepError = (key) => {
    if (key === 'identity') {
      return getBlockError('personal') || (editMode ? '' : getBlockError('account'));
    }
    if (key === 'profile') return getBlockError('type') || getBlockError('contact');
    return getBlockError(key);
  };

  useEffect(() => {
    if (!touched) return;
    setError(getStepError(stepKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched, form, stepKey]);

  const goNext = () => {
    const msg = getStepError(stepKey);
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
    if (!isLastStep) setStepIdx((s) => s + 1);
  };

  const goPrev = () => {
    setError('');
    if (stepIdx > 0) setStepIdx((s) => s - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    for (let i = 0; i < steps.length; i++) {
      const msg = getStepError(steps[i]);
      if (msg) {
        setStepIdx(i);
        setError(msg);
        return;
      }
    }

    const payload = editMode
      ? {
          civility: form.civility,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          phone: form.phone.trim(),
          country: form.country.trim(),
          city: form.city.trim(),
          region: form.region.trim(),
          address: form.address.trim(),
          clientType: form.clientType,
          organizationName: form.organizationName.trim(),
          taxId: form.taxId.trim(),
          contactMode: form.contactMode,
        }
      : {
          civility: form.civility,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          username: form.username.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          country: form.country.trim(),
          city: form.city.trim(),
          region: form.region.trim(),
          address: form.address.trim(),
          password: form.password,
          confirmPassword: form.confirmPassword,
          clientType: form.clientType,
          organizationName: form.organizationName.trim(),
          taxId: form.taxId.trim(),
          contactMode: form.contactMode,
          acceptTerms: form.acceptTerms,
          acceptPrivacy: form.acceptPrivacy,
        };

    const result = await onSubmit(payload);
    if (!result?.ok) {
      setError(t.auth[result?.error] || result?.error || 'Erreur lors de l\'enregistrement.');
      return;
    }

    setSuccess(successMessage || t.auth.registerSuccess);
    setTimeout(() => setSuccess(''), 4000);
    setTimeout(() => {
      if (onSuccess) onSuccess(result);
    }, 1200);
  };

  const Stepper = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0,
        padding: '6px 0 14px',
        borderBottom: '1px solid var(--gray-200)',
        marginBottom: '16px',
      }}
    >
      {steps.map((key, idx) => {
        const n = idx + 1;
        const done = idx < stepIdx;
        const active = idx === stepIdx;
        return (
          <div
            key={key}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: '0.95rem',
                background: active ? 'var(--gold)' : done ? 'var(--teal)' : 'var(--gray-100)',
                color: active ? 'var(--navy-dark)' : done ? '#fff' : 'var(--gray-500)',
                border: `2px solid ${active ? 'var(--gold)' : done ? 'var(--teal)' : 'var(--gray-300)'}`,
              }}
            >
              {done ? <Check size={18} strokeWidth={3} /> : n}
            </div>
            <span
              style={{
                fontSize: '0.78rem',
                fontWeight: 700,
                textAlign: 'center',
                color: active ? 'var(--navy)' : done ? 'var(--teal)' : 'var(--gray-500)',
                maxWidth: '100px',
                lineHeight: '1.2',
              }}
            >
              {stepLabel(key)}
            </span>
            {idx < steps.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  top: 19,
                  right: '-50%',
                  width: 'calc(100% - 42px)',
                  height: 3,
                  borderRadius: 3,
                  background: done ? 'var(--teal)' : 'var(--gray-300)',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderStepBlock = (key) => {
    switch (key) {
      case 'personal':
        return (
          <>
            <div style={rowStyle}>
              <div className="auth-field">
                <label htmlFor="reg-civility">Civilité *</label>
                <div className="auth-input" style={{ paddingRight: 8 }}>
                  <User size={17} aria-hidden="true" />
                  <select
                    id="reg-civility"
                    required
                    value={form.civility}
                    onChange={(e) => update('civility', e.target.value)}
                    style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--navy)', fontSize: '0.95rem' }}
                  >
                    <option value="">Sélectionner</option>
                    {civilities.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="auth-field">
                <label htmlFor="reg-firstName">Prénom *</label>
                <div className="auth-input">
                  <User size={17} aria-hidden="true" />
                  <input
                    id="reg-firstName"
                    required
                    autoComplete="given-name"
                    placeholder="Votre prénom"
                    value={form.firstName}
                    onChange={(e) => update('firstName', e.target.value)}
                  />
                </div>
              </div>
              <div className="auth-field">
                <label htmlFor="reg-lastName">Nom *</label>
                <div className="auth-input">
                  <User size={17} aria-hidden="true" />
                  <input
                    id="reg-lastName"
                    required
                    autoComplete="family-name"
                    placeholder="Votre nom"
                    value={form.lastName}
                    onChange={(e) => update('lastName', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reg-email">Email *</label>
              <div className="auth-input" style={editMode ? { opacity: 0.7 } : undefined}>
                <Mail size={17} aria-hidden="true" />
                <input
                  id="reg-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="email@exemple.com"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  disabled={editMode}
                />
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reg-phone">Téléphone *</label>
              <div className="auth-input">
                <Phone size={17} aria-hidden="true" />
                <input
                  id="reg-phone"
                  type="tel"
                  required
                  autoComplete="tel"
                  placeholder="+216 20 000 000"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
            </div>

            <div style={rowStyle}>
              <div className="auth-field">
                <label htmlFor="reg-country">Pays *</label>
                <div className="auth-input" style={{ paddingRight: 8 }}>
                  <Globe size={17} aria-hidden="true" />
                  <select
                    id="reg-country"
                    required
                    autoComplete="country"
                    value={form.country}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, country: e.target.value, city: '', region: '' }));
                      setTouched(true);
                    }}
                    style={selectStyle}
                  >
                    <option value="">Sélectionnez un pays</option>
                    {allCountries.map((c) => (
                      <option key={c.code} value={c.name}>{c.name}</option>
                    ))}
                    {form.country && !allCountries.some((c) => c.name === form.country) && (
                      <option value={form.country}>{form.country}</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="auth-field">
                <label htmlFor="reg-city">Ville</label>
                <div className="auth-input" style={villeOptions.length ? { paddingRight: 8 } : undefined}>
                  <MapPin size={17} aria-hidden="true" />
                  {villeOptions.length ? (
                    <select
                      id="reg-city"
                      autoComplete="address-level2"
                      value={form.city}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, city: e.target.value, region: '' }));
                        setTouched(true);
                      }}
                      style={selectStyle}
                    >
                      <option value="">Sélectionnez une ville</option>
                      {villeOptions.map((v) => (
                        <option key={v.stateCode || v.name} value={v.name}>{v.name}</option>
                      ))}
                      {form.city && !villeOptions.some((v) => v.name === form.city) && (
                        <option value={form.city}>{form.city}</option>
                      )}
                    </select>
                  ) : (
                    <input
                      id="reg-city"
                      autoComplete="address-level2"
                      placeholder="Tunis"
                      value={form.city}
                      onChange={(e) => update('city', e.target.value)}
                    />
                  )}
                </div>
              </div>
              <div className="auth-field">
                <label htmlFor="reg-region">Région{regionRequired ? ' *' : ''}</label>
                <div className="auth-input" style={regionRequired ? { paddingRight: 8 } : undefined}>
                  <MapPin size={17} aria-hidden="true" />
                  {regionRequired ? (
                    <select
                      id="reg-region"
                      required
                      autoComplete="address-level3"
                      value={form.region}
                      onChange={(e) => update('region', e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Sélectionnez une région</option>
                      {regionOptions.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                      {form.region && !regionOptions.includes(form.region) && (
                        <option value={form.region}>{form.region}</option>
                      )}
                    </select>
                  ) : (
                    <input
                      id="reg-region"
                      autoComplete="address-level3"
                      placeholder="Ex. La Marsa"
                      value={form.region}
                      onChange={(e) => update('region', e.target.value)}
                      disabled={villeOptions.length > 0 && !form.city}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reg-address">Adresse</label>
              <div className="auth-input">
                <MapPin size={17} aria-hidden="true" />
                <input
                  id="reg-address"
                  autoComplete="street-address"
                  placeholder="N°, rue, code postal…"
                  value={form.address}
                  onChange={(e) => update('address', e.target.value)}
                />
              </div>
            </div>
          </>
        );

      case 'account':
        return (
          <>
            <div className="auth-field">
              <label htmlFor="reg-username">Nom d'utilisateur *</label>
              <div className="auth-input">
                <User size={17} aria-hidden="true" />
                <input
                  id="reg-username"
                  required
                  autoComplete="username"
                  placeholder="Votre identifiant"
                  value={form.username}
                  onChange={(e) => update('username', e.target.value)}
                />
              </div>
            </div>

            <div style={rowStyle}>
              <div className="auth-field">
                <label htmlFor="reg-password">Mot de passe *</label>
                <div className="auth-input">
                  <Lock size={17} aria-hidden="true" />
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    minLength={8}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => update('password', e.target.value)}
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 4,
                    background: '#e5e7eb',
                    marginTop: 8,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, getPasswordStrength(form.password) * 20)}%`,
                      background:
                        getPasswordStrength(form.password) <= 2
                          ? '#ef4444'
                          : getPasswordStrength(form.password) <= 3
                            ? '#f59e0b'
                            : getPasswordStrength(form.password) <= 4
                              ? '#3b82f6'
                              : '#10b981',
                      transition: 'all 0.2s ease',
                    }}
                  />
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '6px 0 0',
                    fontSize: '0.72rem',
                    color: 'var(--gray-500)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '2px 10px',
                  }}
                >
                  {[
                    { label: '8 caractères', ok: form.password.length >= 8 },
                    { label: 'Majuscule', ok: /[A-Z]/.test(form.password) },
                    { label: 'Minuscule', ok: /[a-z]/.test(form.password) },
                    { label: 'Chiffre', ok: /[0-9]/.test(form.password) },
                    { label: 'Spécial', ok: /[^A-Za-z0-9]/.test(form.password) },
                  ].map((item) => (
                    <li key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4, color: item.ok ? '#10b981' : 'var(--gray-500)' }}>
                      <span style={{ fontSize: '0.75rem' }}>{item.ok ? '✓' : '○'}</span>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="auth-field">
                <label htmlFor="reg-confirm">Confirmer le mot de passe *</label>
                <div className="auth-input">
                  <Lock size={17} aria-hidden="true" />
                  <input
                    id="reg-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    minLength={8}
                    placeholder="••••••••"
                    value={form.confirmPassword}
                    onChange={(e) => update('confirmPassword', e.target.value)}
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? t.auth.hidePassword : t.auth.showPassword}
                  >
                    {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'type':
        return (
          <>
            <div className="auth-field">
              <span style={{ display: 'block', marginBottom: '8px', fontSize: '0.84rem', fontWeight: '700', color: 'var(--navy)' }}>
                Vous êtes : *
              </span>
              <div style={radioGroupStyle}>
                {['particulier', 'entreprise', 'organisation'].map((type) => (
                  <label
                    key={type}
                    style={radioStyle(form.clientType === type)}
                  >
                    <input
                      type="radio"
                      name="clientType"
                      value={type}
                      checked={form.clientType === type}
                      onChange={() => update('clientType', type)}
                      style={{ width: 18, height: 18, accentColor: 'var(--teal)' }}
                    />
                    {type === 'particulier' && 'Particulier'}
                    {type === 'entreprise' && 'Entreprise'}
                    {type === 'organisation' && 'Organisation'}
                  </label>
                ))}
              </div>
            </div>

            {form.clientType !== 'particulier' && (
              <>
                <div className="auth-field" style={{ marginTop: '12px' }}>
                  <label htmlFor="reg-organization">Nom de l'entreprise / organisation</label>
                  <div className="auth-input">
                    <Building2 size={17} aria-hidden="true" />
                    <input
                      id="reg-organization"
                      placeholder="Raison sociale"
                      value={form.organizationName}
                      onChange={(e) => update('organizationName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label htmlFor="reg-taxId">
                    Matricule fiscal ou identifiant professionnel (optionnel)
                  </label>
                  <div className="auth-input">
                    <Briefcase size={17} aria-hidden="true" />
                    <input
                      id="reg-taxId"
                      placeholder="Optionnel"
                      value={form.taxId}
                      onChange={(e) => update('taxId', e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        );

      case 'contact':
        return (
          <div className="auth-field">
            <span style={{ display: 'block', marginBottom: '8px', fontSize: '0.84rem', fontWeight: '700', color: 'var(--navy)' }}>
              Mode de contact préféré :
            </span>
            <div style={radioGroupStyle}>
              {[
                { value: 'email', label: 'Email' },
                { value: 'telephone', label: 'Téléphone' },
                { value: 'les-deux', label: 'Les deux' },
              ].map((option) => (
                <label key={option.value} style={radioStyle(form.contactMode === option.value)}>
                  <input
                    type="radio"
                    name="contactMode"
                    value={option.value}
                    checked={form.contactMode === option.value}
                    onChange={() => update('contactMode', option.value)}
                    style={{ width: 18, height: 18, accentColor: 'var(--teal)' }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        );

      case 'terms':
        return (
          <div className="auth-field" style={{ display: 'grid', gap: '10px' }}>
            <label style={checkboxStyle}>
              <input
                type="checkbox"
                checked={form.acceptTerms}
                onChange={(e) => update('acceptTerms', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--teal)', marginTop: '2px' }}
              />
              J'accepte les conditions générales d'utilisation. *
            </label>
            <label style={checkboxStyle}>
              <input
                type="checkbox"
                checked={form.acceptPrivacy}
                onChange={(e) => update('acceptPrivacy', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--teal)', marginTop: '2px' }}
              />
              J'accepte la politique de confidentialité. *
            </label>
            <label style={checkboxStyle}>
              <input
                type="checkbox"
                checked={form.confirmInfo}
                onChange={(e) => update('confirmInfo', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--teal)', marginTop: '2px' }}
              />
              Je confirme l'exactitude des informations fournies. *
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  const stepContent = (() => {
    switch (stepKey) {
      case 'identity':
        return (
          <>
            {renderStepBlock('personal')}
            {!editMode && (
              <>
                <div style={sectionDividerStyle}>Compte</div>
                {renderStepBlock('account')}
              </>
            )}
          </>
        );
      case 'profile':
        return (
          <>
            {renderStepBlock('type')}
            <div style={sectionDividerStyle}>Communication</div>
            {renderStepBlock('contact')}
          </>
        );
      case 'terms':
        return renderStepBlock('terms');
      default:
        return null;
    }
  })();

  return (
    <>
      {success && (
        <div className="auth-toast" role="status">
          <CheckCircle2 size={18} />
          <span>{success}</span>
        </div>
      )}
    <form className="auth-card auth-card--wide" onSubmit={(e) => e.preventDefault()} noValidate style={{ width: '100%' }}>
      {(title || subtitle) && (
        <div className="auth-card-head" style={{ marginBottom: 16 }}>
          {title && <h2 style={{ fontSize: '1.35rem' }}>{title}</h2>}
          {subtitle && <p style={{ fontSize: '0.85rem' }}>{subtitle}</p>}
        </div>
      )}

      <Stepper />

      {error && <div className="auth-error" role="alert" style={{ marginBottom: 12, padding: '10px 12px' }}>{error}</div>}

      <div>
        {stepContent}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', gap: '12px' }}>
        <button
          type="button"
          className="btn btn-outline"
          onClick={goPrev}
          disabled={stepIdx === 0}
          style={{
            background: stepIdx === 0 ? 'var(--gray-100)' : '#fff',
            borderColor: stepIdx === 0 ? 'var(--gray-300)' : 'var(--teal)',
            color: stepIdx === 0 ? 'var(--gray-500)' : 'var(--teal)',
          }}
        >
          <ChevronLeft size={18} />
          Précédent
        </button>

        {isLastStep ? (
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>
            {editMode ? <Check size={18} /> : <UserPlus size={18} />}
            {submitLabel || (editMode ? t.dashboard?.save || 'Enregistrer' : t.auth.registerButton)}
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={goNext}>
            Suivant
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {footer}
    </form>
    </>
  );
}
